import { z } from 'zod';

/**
 * What a reviewer decided a report amounted to.
 *
 * Recorded **separately from the action they then took**, and that separation is
 * the point. Suspending and banning already happen through
 * `UserModerationService` (N9/N20) and are audited there; this field is the
 * reviewer's *verdict on the report*, which is what the next reviewer needs when
 * the same member is reported again next month.
 *
 * - `upheld` — the report was right about something. Whatever enforcement
 *   followed is a separate, audited act.
 * - `no_action` — nothing here breaks the rules. Not a judgement on the
 *   reporter; most honest reports about somebody unpleasant land here.
 * - `invalid` — the report is not about conduct at all: a mis-tap, a test, an
 *   empty `other`. Kept distinct from `no_action` because a member who files
 *   these repeatedly is a different problem from one who reports in good faith
 *   and is wrong, and P27's abuse work will need to tell them apart.
 */
export const REPORT_RESOLUTIONS = ['upheld', 'no_action', 'invalid'] as const;

export const ReportResolutionSchema = z.enum(REPORT_RESOLUTIONS);

export type ReportResolution = (typeof REPORT_RESOLUTIONS)[number];

/**
 * **No verdict keeps a member hidden, `upheld` included** — and the absence of a
 * predicate saying otherwise is deliberate, so nobody adds one back.
 *
 * `moderation.autoHideReportThreshold` hides a member *while reports are open*:
 * it is a holding measure for the window before a human has looked, and the
 * setting's own documentation says hiding is "reversible and not an enforcement
 * action". Once every report against a member is resolved the hide lifts,
 * whatever the reviewer concluded.
 *
 * The tempting rule — "keep them hidden if the report was upheld" — produces a
 * member who is invisible to the whole platform, indefinitely, with no status
 * change, no audit entry, no notice and nothing to appeal: a shadow ban that no
 * part of this system models. A reviewer who wants somebody out of circulation
 * suspends or bans them, which goes through `UserStatusService` (N9), is audited,
 * and is appealable. That the verdict and the hiding are decoupled is what keeps
 * `upheld` an honest record of what a reviewer thought rather than a hidden
 * second enforcement path.
 */
